
const g:any = window[ENV.projectName] = window[ENV.projectName] || {};

import { Component, Vue, Watch } from 'vue-property-decorator';
import gsap, { Expo, Cubic, Sine } from 'gsap/gsap-core';

import { mainStore } from '../_store/main';

import { ShapeKeys, PointCloudDefinition } from '../_modules/webgl/PointCloud';

import PageComponent from '../_modules/PageComponent';
import MainGL from '../_modules/webgl/MainGL';
import ServiceIegao from '../_modules/ServiceIegao';

import OnScreenImg from '../_modules/OnScreenImg';
import OnScreenItem from '../_modules/OnScreenItem';


const ServiceScrollState = {
  KV: 'KV',
  VISION: 'VISION',
  TECHNOLOGY: 'TECHNOLOGY',
  CONTACT: 'CONTACT',
} as const;

export const ServiceGLDefinition: PointCloudDefinition = {
  KV: {
    key: 'KV',
    shapeKey: 'KV',
    isKV: true,
    order: 0,
    kvItems: [
      { key: '', shapeKey: ShapeKeys.IEGAO, order: 0 },
   ]
  },
  TECHNOLOGY : { key: 'TECHNOLOGY' , shapeKey: ShapeKeys.BRAIN   , order: 1 },
  CONTACT    : { key: 'CONTACT'    , shapeKey: ShapeKeys.CONTACT , order: 2 },
};


export default class Main extends PageComponent {
  protected memberEl!: HTMLElement;

  protected technologyEl!: HTMLElement;
  protected technologyImgEl!: HTMLElement;
  protected technologyObserver!: IntersectionObserver;

  protected iegao!: ServiceIegao;


  constructor() {
    super();
    this.init();
  }

  get isKV      () { return this.scrollState === ServiceScrollState.KV      }
  get isService () { return this.scrollState === ServiceScrollState.TECHNOLOGY }
  get isContact () { return this.scrollState === ServiceScrollState.CONTACT }

  protected async initObservers() {
    await this.wait(400);

    this.initPageKVObserver();
    this.initTechnologyObserver();
    this.initContactLinkObserver();
  }

  protected async initTechnology() {
    this.technologyEl = this.bodyElm.querySelector('.js-serviceTechnology') as HTMLElement;
    this.technologyImgEl = this.technologyEl.querySelector('.js-serviceTechnologyImg') as HTMLElement;
  }

  protected initTechnologyObserver() {
    if(!window.IntersectionObserver || g.isGoogleBot) return;

    this.technologyObserver = new IntersectionObserver((entries)=> {
      const entry = entries[0];
      if(entry.isIntersecting) {
        if(this.scrollState === '' ||
          (!this.isService &&
            ((this.scrollDir === 1 && this.isKV) || (this.scrollDir === -1 && !this.isKV))
          )
        ) {
          this.scrollState = ServiceScrollState.TECHNOLOGY;
          console.log('scroll state:', ServiceScrollState.TECHNOLOGY);
          this.mainGL.setMode(ServiceScrollState.TECHNOLOGY);

        }
      }
    }, {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      rootMargin: '-10% 0px'
    });
    this.technologyObserver.observe(this.technologyEl);
  }

  protected async init(): Promise<void> {
    mainStore.setCurrentPageId('service');

    this.scrollToTweenObj = { value: window.scrollY || window.pageYOffset };

    await Promise.all([
      g.loadFontPromise,
      this.initMainGL(),
      this.initLoading(),
      this.initHeader(),
      this.initFooter(),
      this.initMenu(),
    ]);

    await this.initContents();

    this.initMediaQuery();
    this.initScroll();
    this.initResize();

    this.resizeHandler();

    mainStore.setScrolled(false);
    mainStore.setLoaded(true);

    this.initObservers();

    await this.pageKV.$nextTick();

    this.isLocked = false;
    this.onResize();
    this.onScroll();
    this.play();
  }

  protected async initContents(): Promise<void> {
    await Promise.all([
      this.initPageKV(),
      this.initTechnology(),
      this.initContact(),
      this.initIegao(),
    ]);

    await super.initContents();
  }

  protected async initIegao() {
    this.iegao = new ServiceIegao({
      el: this.bodyElm.querySelector('.js-serviceIegao') as HTMLElement
    });
    await this.iegao.$nextTick();

    const item: OnScreenImg = new OnScreenImg({ el: this.iegao.$el.querySelector('.js-iegaoOnScreenImg') as HTMLElement });
    await this.onScreenItemManager.initItem(item);

    const onScreenEls = this.iegao.$el.querySelectorAll('.js-iegaoOnScreenItem')
    const promises: Promise<any>[] = [];
    for (let i = 0; i < onScreenEls.length; i++) {
      const item: OnScreenItem = new OnScreenItem({ el: onScreenEls[i] });
      promises.push(this.onScreenItemManager.initItem(item));
    }

    return Promise.all(promises);
  }

  protected async initMainGL() {
    const mainGLEl = this.bodyElm.querySelector('.js-mainGL') as HTMLElement;

    this.mainGL = new MainGL<typeof ServiceGLDefinition>(
      mainGLEl,
      mainGLEl.querySelector('canvas') as HTMLCanvasElement,
      ServiceGLDefinition,
      'KV',
      false
    );

    await this.mainGL.init()
    .catch((e)=> {
      console.log(e);
      this.htmlElm.classList.add('is-noWebGL');
      mainStore.setNoWebGL(true);
    })
    this.mainGL.onResize();
  }

  protected async onResize() {
    this.iegao.onResize();
    await this.iegao.$nextTick();

    this.mainGL.onResize();
    this.updateGLScales();
  }

  public updateScrollerScrolltop() {
    if(g.isiOS) return;
    gsap.killTweensOf(this.scroller);
    const to = Math.min(this.scrollHeight, Math.max(0, window.scrollY || window.pageYOffset));
    gsap.to(this.scroller, 0.8, { ease: Expo.easeOut, y: -to });
    this.iegao.scroll(to);
  }

  protected onScroll(): void {
    super.onScroll();
    this.mainGL.setScrollTop();
  }

  protected updateGLScales() {
    const kvImgEl = this.pageKV.getImgContainer()

    this.mainGL.setParams({
      KV: {
        imgSize: kvImgEl.offsetWidth || 1,
        offset: this.getOffsetImgItem(kvImgEl, true)
      },
      TECHNOLOGY: {
        imgSize: this.technologyImgEl.offsetWidth,
        offset: this.getOffsetImgItem(this.technologyImgEl)
      },
      CONTACT: {
        imgSize: this.contactLinkImgEl.offsetWidth,
        offset: this.getOffsetImgItem(this.contactLinkImgEl)
      },
    })
  }

  public play(): void {
    super.play();

    this.mainGL.startLoop();
    this.updateGLScales();

    this.mainGL.play(()=> {
      this.isLocked = false;
      this.mainGL.setMode('KV');
      this.mainGL.setKVIndex(0);
      this.pageKV.play();
    }, false);
  }
}